import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  drawOverlay,
  extrapolateLanding,
  fitTrajectory,
  processVideoFile,
  startLiveDetection,
  type AnalysisResult,
  type LandingEstimate,
  type Point,
  type Trajectory,
} from "../lib/ballFlightProcessing";
import { makeWorkCanvas } from "../lib/ballDetector";

type Mode = "video" | "live";
type VideoState = "idle" | "ready" | "processing" | "done";
type LiveState = "idle" | "starting" | "recording" | "done" | "denied";

export function BallFlightAnalysis() {
  useParams(); // clubId not needed for logic, but keeps this route club-scoped like the rest of the app
  const [mode, setMode] = useState<Mode>("video");

  return (
    <main className="mx-auto min-h-dvh max-w-md px-5 pb-24 pt-6">
      <h1 className="font-display text-[1.75rem] font-semibold tracking-tight text-turf-900">Ballflug-Analyse</h1>
      <p className="mt-1 text-sm text-ink-600">
        Zeichnet automatisch eine Flugkurve über dein Video. Experimentelles Feature, keine exakte Messung.
      </p>

      <div role="tablist" className="mt-5 grid grid-cols-2 gap-1 rounded-xl bg-chalk-200/70 p-1">
        <button
          role="tab"
          aria-selected={mode === "video"}
          onClick={() => setMode("video")}
          className={`h-10 rounded-lg text-sm font-medium transition-all ${
            mode === "video" ? "bg-white text-turf-800 shadow-sm" : "text-ink-600"
          }`}
        >
          Video hochladen
        </button>
        <button
          role="tab"
          aria-selected={mode === "live"}
          onClick={() => setMode("live")}
          className={`h-10 rounded-lg text-sm font-medium transition-all ${
            mode === "live" ? "bg-white text-turf-800 shadow-sm" : "text-ink-600"
          }`}
        >
          Live-Kamera
        </button>
      </div>

      <div className="mt-5" key={mode}>
        {mode === "video" ? <VideoUploadTracker /> : <LiveCameraTracker />}
      </div>
    </main>
  );
}

function VideoUploadTracker() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const workCanvasRef = useRef<HTMLCanvasElement>(makeWorkCanvas());

  const [state, setState] = useState<VideoState>("idle");
  const [progress, setProgress] = useState(0);
  const [modelLoading, setModelLoading] = useState(false);
  const [points, setPoints] = useState<Point[]>([]);
  const [curve, setCurve] = useState<{ fn: Trajectory | null }>({ fn: null });
  const [landing, setLanding] = useState<LandingEstimate | null>(null);
  const [unreliable, setUnreliable] = useState(false);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [playWithOverlay, setPlayWithOverlay] = useState(false);
  const cancelledRef = useRef(false);

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setPoints([]);
    setCurve({ fn: null });
    setLanding(null);
    setUnreliable(false);
    setPlayWithOverlay(false);
    if (fileUrl) URL.revokeObjectURL(fileUrl);
    setFileUrl(URL.createObjectURL(file));
    setState("ready");
  }

  function resizeCanvasToVideo() {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.clientWidth;
    canvas.height = video.clientHeight;
  }

  async function handleAnalyze() {
    const video = videoRef.current;
    if (!video) return;
    setState("processing");
    setProgress(0);
    setError(null);
    cancelledRef.current = false;
    try {
      if (!video.videoWidth || !video.videoHeight) {
        // A fixed-length race here used to give up and analyze anyway even
        // with no real dimensions yet on a slow decode (large file, older
        // phone) — silently producing an empty/garbage result on the first
        // attempt, which then looked fine on a retry once the browser had
        // already finished loading it in the background. Poll for the real
        // dimensions instead of racing a clock, and fail loudly rather than
        // silently proceeding broken if they never show up.
        const deadline = Date.now() + 15000;
        while ((!video.videoWidth || !video.videoHeight) && Date.now() < deadline) {
          await new Promise<void>((resolve) => {
            function onLoaded() {
              video?.removeEventListener("loadedmetadata", onLoaded);
              resolve();
            }
            video.addEventListener("loadedmetadata", onLoaded);
            setTimeout(resolve, 300);
          });
        }
        if (!video.videoWidth || !video.videoHeight) {
          throw new Error("video metadata never loaded");
        }
      }
      video.pause();
      const result: AnalysisResult = await processVideoFile(
        video,
        workCanvasRef.current,
        setProgress,
        () => cancelledRef.current,
        (status) => setModelLoading(status === "loading-model")
      );
      if (cancelledRef.current) {
        setState("ready");
        return;
      }
      if (result.points.length < 3) {
        setError(
          "Konnte den Ball nicht zuverlässig erkennen. Versuch ein Video mit klarem Himmel-/Grashintergrund, ruhig gehaltener Kamera (am besten Stativ/abgelegt statt Hand) und dem Ball gut sichtbar im Bild."
        );
        setPoints(result.points);
        setCurve({ fn: result.curve });
        setLanding(null);
        setUnreliable(true);
        setState("done");
        return;
      }
      setPoints(result.points);
      setCurve({ fn: result.curve });
      setLanding(result.curve ? extrapolateLanding(result.curve, result.points) : null);
      setUnreliable(result.unreliable);
      setState("done");
      video.currentTime = 0;
    } catch {
      setError("Analyse fehlgeschlagen. Bitte anderes Video versuchen.");
      setState("ready");
    }
  }

  function handleCancelAnalysis() {
    cancelledRef.current = true;
  }

  // Draw static overlay once analysis is done
  useEffect(() => {
    if (state !== "done" || playWithOverlay) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    resizeCanvasToVideo();
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    drawOverlay(ctx, canvas.width, canvas.height, points, curve.fn, landing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, points, curve, landing, playWithOverlay]);

  // Progressive overlay during playback
  useEffect(() => {
    if (!playWithOverlay) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    resizeCanvasToVideo();
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let raf = 0;

    function tick() {
      if (!video || !ctx || !canvas) return;
      const visible = points.filter((p) => p.t <= video.currentTime + 0.05);
      drawOverlay(ctx, canvas.width, canvas.height, visible, curve.fn, landing);
      raf = requestAnimationFrame(tick);
    }
    video.currentTime = 0;
    video.play();
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playWithOverlay, points, curve, landing]);

  function reset() {
    setState("idle");
    setPoints([]);
    setCurve({ fn: null });
    setLanding(null);
    setUnreliable(false);
    setError(null);
    setPlayWithOverlay(false);
    if (fileUrl) URL.revokeObjectURL(fileUrl);
    setFileUrl(null);
  }

  return (
    <div>
      {state === "idle" && (
        <label
          htmlFor="ball-video-input"
          className="flex h-40 cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-chalk-200 bg-white text-sm text-ink-400 transition-colors hover:border-turf-400"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="mb-2 h-8 w-8">
            <path d="M23 7l-7 5 7 5V7Z" />
            <rect x="1" y="5" width="15" height="14" rx="2" />
          </svg>
          Video auswählen
        </label>
      )}
      <input id="ball-video-input" type="file" accept="video/*" onChange={handleFile} className="sr-only" />

      {state !== "idle" && (
        <div className="card overflow-hidden rounded-2xl">
          <div className="relative bg-ink-900">
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video
              ref={videoRef}
              src={fileUrl ?? undefined}
              onLoadedMetadata={resizeCanvasToVideo}
              playsInline
              muted
              controls={playWithOverlay}
              className="block w-full"
            />
            <canvas ref={canvasRef} className="pointer-events-none absolute inset-0 h-full w-full" />
          </div>

          <div className="p-4">
            {state === "ready" && (
              <button
                onClick={handleAnalyze}
                className="h-11 w-full rounded-xl bg-turf-700 text-sm font-semibold text-chalk-50"
              >
                Analyse starten
              </button>
            )}

            {state === "processing" && (
              <div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-chalk-200">
                  <div
                    className="h-full rounded-full bg-turf-600 transition-all"
                    style={{ width: `${Math.round(progress * 100)}%` }}
                  />
                </div>
                <p className="mt-2 text-center text-xs text-ink-400">
                  {modelLoading
                    ? "Erkennungsmodell wird geladen (einmalig)…"
                    : `Analysiere Video… ${Math.round(progress * 100)}%`}
                </p>
                <button
                  onClick={handleCancelAnalysis}
                  className="mt-3 h-9 w-full rounded-xl border border-chalk-200 bg-white text-sm font-medium text-ink-600"
                >
                  Abbrechen
                </button>
              </div>
            )}

            {state === "done" && (
              <div className="space-y-2">
                {!error && !unreliable && (
                  <p className="text-center text-xs text-ink-400">{points.length} Ballpositionen erkannt (Kurve konsistent)</p>
                )}
                {!error && !unreliable && landing && (
                  <p className="text-center text-xs text-ink-400">
                    Gestrichelt: geschätzte Landung (Hochrechnung, keine Messung)
                  </p>
                )}
                {!error && unreliable && (
                  <p className="rounded-lg bg-amber-100 px-3 py-2 text-center text-xs text-amber-500">
                    Keine konsistente Flugbahn gefunden – die Kurve ist unsicher. Am besten Kamera ruhig halten (Stativ/ablegen) und Video erneut versuchen.
                  </p>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => setPlayWithOverlay((v) => !v)}
                    disabled={points.length < 3}
                    className="h-11 flex-1 rounded-xl bg-turf-700 text-sm font-semibold text-chalk-50 disabled:opacity-50"
                  >
                    {playWithOverlay ? "Standbild zeigen" : "Mit Kurve abspielen"}
                  </button>
                  <button onClick={reset} className="h-11 flex-1 rounded-xl border border-chalk-200 bg-white text-sm font-semibold text-ink-900">
                    Neues Video
                  </button>
                </div>
              </div>
            )}

            {error && <p className="mt-3 rounded-lg bg-flag-100 px-3 py-2 text-sm text-flag-600">{error}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function LiveCameraTracker() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const workCanvasRef = useRef<HTMLCanvasElement>(makeWorkCanvas());
  const stopRef = useRef<(() => void) | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const pointsRef = useRef<Point[]>([]);

  const [state, setState] = useState<LiveState>("idle");
  const [pointCount, setPointCount] = useState(0);

  useEffect(() => {
    return () => {
      stopRef.current?.();
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  async function startCamera() {
    setState("starting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      pointsRef.current = [];
      setPointCount(0);

      stopRef.current = startLiveDetection(
        videoRef.current!,
        workCanvasRef.current,
        (point) => {
          pointsRef.current = [...pointsRef.current, point];
          setPointCount(pointsRef.current.length);
          const canvas = canvasRef.current;
          const video = videoRef.current;
          if (canvas && video) {
            canvas.width = video.clientWidth;
            canvas.height = video.clientHeight;
            const ctx = canvas.getContext("2d");
            if (ctx) drawOverlay(ctx, canvas.width, canvas.height, pointsRef.current, null);
          }
        },
        () => setState("recording")
      );
    } catch {
      setState("denied");
    }
  }

  function stopRecording() {
    stopRef.current?.();
    stopRef.current = null;
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        const curve = fitTrajectory(pointsRef.current);
        const landing = curve ? extrapolateLanding(curve, pointsRef.current) : null;
        drawOverlay(ctx, canvas.width, canvas.height, pointsRef.current, curve, landing);
      }
    }
    setState("done");
  }

  function newRecording() {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
    }
    pointsRef.current = [];
    setPointCount(0);
    setState("recording");
    stopRef.current = startLiveDetection(videoRef.current!, workCanvasRef.current, (point) => {
      pointsRef.current = [...pointsRef.current, point];
      setPointCount(pointsRef.current.length);
      const c = canvasRef.current;
      const v = videoRef.current;
      if (c && v) {
        c.width = v.clientWidth;
        c.height = v.clientHeight;
        const ctx = c.getContext("2d");
        if (ctx) drawOverlay(ctx, c.width, c.height, pointsRef.current, null);
      }
    });
  }

  return (
    <div>
      {state === "idle" && (
        <button
          onClick={startCamera}
          className="card card-interactive flex w-full flex-col items-center gap-3 rounded-2xl p-8 text-center"
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-turf-100 text-turf-700">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-7 w-7">
              <rect x="2" y="6" width="14" height="12" rx="2" />
              <path d="M16 10l6-3v10l-6-3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className="font-semibold text-ink-900">Kamera starten</span>
          <span className="text-xs text-ink-400">Am besten Handy quer halten, Ball und Flugbahn im Bild.</span>
        </button>
      )}

      {state === "denied" && (
        <div className="card rounded-2xl p-6 text-center">
          <p className="text-sm text-flag-600">Kamerazugriff wurde nicht erteilt.</p>
          <p className="mt-1 text-xs text-ink-400">Bitte in den Browser-Einstellungen erlauben und erneut versuchen.</p>
          <button onClick={startCamera} className="mt-4 h-10 w-full rounded-xl bg-turf-700 text-sm font-semibold text-chalk-50">
            Erneut versuchen
          </button>
        </div>
      )}

      {(state === "starting" || state === "recording" || state === "done") && (
        <div className="card overflow-hidden rounded-2xl">
          <div className="relative min-h-[280px] bg-ink-900">
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video ref={videoRef} playsInline muted className="block w-full" />
            <canvas ref={canvasRef} className="pointer-events-none absolute inset-0 h-full w-full" />
            {state === "starting" && (
              <div className="absolute inset-0 flex items-center justify-center bg-ink-900/70 px-6 text-center text-sm text-chalk-50">
                Kamera wird gestartet – Erkennungsmodell lädt (einmalig)…
              </div>
            )}
            {state === "recording" && (
              <span className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-flag-600/90 px-2.5 py-1 text-xs font-semibold text-chalk-50">
                <span className="h-1.5 w-1.5 rounded-full bg-chalk-50" /> Aufnahme
              </span>
            )}
          </div>
          {state !== "starting" && (
            <div className="p-4">
              <p className="mb-2 text-center text-xs text-ink-400">{pointCount} Ballpositionen erkannt</p>
              {state === "recording" ? (
                <button onClick={stopRecording} className="h-11 w-full rounded-xl bg-flag-600 text-sm font-semibold text-chalk-50">
                  Stopp
                </button>
              ) : (
                <button onClick={newRecording} className="h-11 w-full rounded-xl bg-turf-700 text-sm font-semibold text-chalk-50">
                  Neue Aufnahme
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
